import { Injectable, signal, WritableSignal } from '@angular/core';
import { Campaign, Coupon } from '@shared/factory';

export enum CouponsTab {
  CURRENT = 0,
  TO_DISCOVER = 1,
  FINALIZED = 2,
}

export interface CampaignReward {
  campaign: Campaign;
  reward: number | null; // `null` when it could not be fetched
}

/**
 * Single source of truth for the coupons currently displayed on the coupons page.
 *
 * The page seeds this signal from its fetch resource and renders its tab buckets
 * from it, while the coupon card mutates it after a successful activation. This
 * mirrors how challenge selection and reward activation update their lists, and
 * works around `NgComponentOutlet` not forwarding `output()` events from the card.
 */
@Injectable({
  providedIn: 'root',
})
export class CouponsStateService {
  readonly coupons = signal<Coupon[]>([]);

  /**
   * Campaign `coupons` was seeded from, `null` while empty. The list survives
   * navigation, so callers reusing it as a cache must check it still belongs to
   * the campaign on screen.
   */
  readonly couponsCampaignId: WritableSignal<number | null> = signal(null);

  /**
   * Offer ids activated during the current visit.
   *
   * An activated coupon keeps its slot in the "À activer" bucket — with the
   * activated visual — instead of jumping straight to "Activées": moving it
   * immediately made the tile vanish under the user's finger with no confirmation
   * that the activation worked. The set is emptied on every re-seed, so only a
   * browser reload finally moves the coupon to its real tab.
   */
  readonly pendingActivations = signal<ReadonlySet<number>>(new Set());

  /**
   * Tab selected on the coupons page, `null` until one is picked (by the user
   * or by the landing-tab default). Root-scoped on purpose: the page component
   * is destroyed on every navigation, so keeping this locally would silently
   * send the user back to the default bucket on the way back from the help
   * page — the modal it replaced never had that problem.
   */
  readonly activeTab: WritableSignal<CouponsTab | null> = signal(null);

  // Wallet "cette année" figure, also the total of the savings details modal's
  // "Année en cours" tab. `null` until loaded, and when the call failed.
  readonly yearlyReward: WritableSignal<number | null> = signal(null);
  readonly yearlyRewardLoaded: WritableSignal<boolean> = signal(false);

  // Savings details modal ("Année en cours" tab): reward of each completed
  // campaign that ended this year, fetched when that tab opens. All loaded once
  // none is `null`; until then, opening the tab fetches the missing ones again.
  readonly rewardsPerCampaign: WritableSignal<CampaignReward[]> = signal([]);
  readonly allRewardsPerCampaignLoaded: WritableSignal<boolean> = signal(false);

  setCoupons(coupons: Coupon[], campaignId: number | null = null): void {
    const visibleCoupons = coupons.filter(
      (coupon: Coupon) => coupon.isLoaded || coupon.inStock
    );
    this.coupons.set(visibleCoupons);
    this.pendingActivations.set(new Set());
    this.couponsCampaignId.set(campaignId);
  }

  /**
   * Mark a coupon as activated in place. Clones the matching instance so the
   * `Coupon` prototype/getters (e.g. `isLoaded`) are preserved while creating a
   * new reference for change detection — same approach as `updateSelectedChallenges`.
   *
   * Also records the offer id in `pendingActivations` so the coupon stays in the
   * tab the user activated it from.
   */
  markLoaded(offerId: number, loadingDate: string): void {
    this.pendingActivations.update((ids) => new Set(ids).add(offerId));
    this.coupons.update((list) =>
      list.map((coupon) => {
        if (coupon.offerId !== offerId) {
          return coupon;
        }
        const clone: Coupon = Object.assign(
          Object.create(Object.getPrototypeOf(coupon)),
          coupon
        );
        clone.loadingDate = loadingDate;
        clone.remainingSelections = Math.max(0, clone.remainingSelections - 1);
        return clone;
      })
    );
  }
}
